/* eslint-disable @atlaskit/editor/no-re-export */

import type { Transformer } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Schema } from '@atlaskit/editor-prosemirror/model';

import {
	adfToMarkdown,
	markdownToAdf,
	type MarkdownConversionOptions,
} from './markdown-conversion';

/**
 * Product-specific hooks for the GFM transformer: the same options as
 * `MarkdownConversionOptions`. `serializeFileMedia` provides a stable viewer link for
 * file media, `serializeMention` links structured mentions to identity URLs when readable handlers
 * are enabled, and `hardBreakAsSpaces` emits hard breaks as two trailing spaces.
 */
export type GfmMarkdownTransformerOptions = MarkdownConversionOptions;

/**
 * Creates a bidirectional GFM {@link Transformer} for the supplied editor
 * schema.
 *
 * This is the storage/output transformer for a Markdown-compatible WYSIWYG
 * editor. It deliberately does not mount Markdown Mode, CodeMirror, or any
 * source-view UI. Use it with `replaceDocument(markdown, { transformer })`,
 * `requestDocumentAsync({ transformer })`, or `contentTransformerProvider`.
 *
 * GFM tables, task lists, and strikethrough use the same parser and serializer
 * configuration as Markdown Mode. Encoding rejects unsupported top-level
 * ProseMirror nodes rather than silently dropping their content; consumers
 * should handle that error before persisting output.
 */
export const createGfmMarkdownTransformer = (
	schema: Schema,
	options: GfmMarkdownTransformerOptions = {},
): Transformer<string> => ({
	encode: (node) => adfToMarkdown(node, schema, options).markdown,
	parse: (markdown) => markdownToAdf(markdown, schema),
});
